import React, { FunctionComponent } from "react";
import { ACard, AStringCurrentGauge } from "@atiautomacao/ati-ui-library";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import BatteryChargingFullOutlinedIcon from "@mui/icons-material/BatteryChargingFullOutlined";
import { getIconColor } from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import { MenuItemStatus } from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import { useBessBmsSoc } from "./BessBmsSocReactQuery";
import {
    BMS_LABEL_PREFIX,
    BMS_LEGEND_ITEMS,
    buildBmsSummary,
    formatBmsNumber,
    resolveFillRatio,
    resolveGaugeVisualState,
} from "./BessBmsSocUtils";

const SECTION_TITLE = "SOC por Rack de Baterias";
const EMPTY_MESSAGE = "Não existem racks configurados para exibição.";
const ERROR_MESSAGE = "Não foi possível carregar os racks de SOC. Tente novamente.";
const TITLE_ICON_COLOR = "#2D5DE7";
const SOC_UNIT = "%";

// Layout dos gauges. Com poucos racks (<= LEFT_ALIGN_THRESHOLD) ficam alinhados à esquerda em
// largura fixa (espaçamento mínimo); acima disso, distribuem e preenchem toda a largura do card.
const LEFT_ALIGN_THRESHOLD = 3;
const GAUGE_ITEM_WIDTH = 140; // px — largura de cada gauge no layout à esquerda (poucos racks)

// > 3 racks: grid responsiva que distribui e preenche o card (auto-fit + gauge com width:100%).
const GAUGES_GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(122px, 1fr))",
    gap: "12px",
} as const;

// <= 3 racks: alinhados à esquerda, largura fixa, com espaçamento mínimo entre eles.
const GAUGES_LEFT_SX = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: "12px",
} as const;

// Legenda (Normalizado / Alarmado / Sem dados) — ícone 20px, igual ao PCS.
const Legend: FunctionComponent = () => (
    <Stack direction="row" spacing={2} sx={{ alignItems: "center", padding: "8px", flexWrap: "wrap" }}>
        {BMS_LEGEND_ITEMS.map((item) => (
            <Stack key={item.label} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <FiberManualRecordIcon
                    sx={{ color: getIconColor(item.status as MenuItemStatus), fontSize: 20 }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{item.label}</Typography>
            </Stack>
        ))}
    </Stack>
);

const BessBmsSocSection: FunctionComponent<{ equipmentId: number }> = ({ equipmentId }) => {
    const { data, isLoading, isError } = useBessBmsSoc(equipmentId);
    const racks = data?.data ?? [];
    // RN3b: num refresh automático que falha o react-query preserva o cache. Só tratamos como erro
    // quando não há dado algum — do contrário os gauges desmontariam a cada blip da API.
    const hasCachedData = data != null;

    // Subtítulo só quando há resposta (RN4b: "0 racks monitorados" também aparece). Em erro/loading, oculto.
    const subtitle = !isLoading && (hasCachedData || !isError) ? buildBmsSummary(racks) : "";

    let content: JSX.Element;
    if (isLoading) {
        content = (
            <Box sx={GAUGES_GRID_SX}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} animation="wave" variant="rounded" width="100%" height={140} />
                ))}
            </Box>
        );
    } else if (isError && !hasCachedData) {
        content = (
            <Typography sx={{ fontSize: 14, color: "error.main", padding: "8px 0" }}>
                {ERROR_MESSAGE}
            </Typography>
        );
    } else if (racks.length === 0) {
        // RN4: a seção permanece visível informando que não há racks configurados.
        content = (
            <Typography sx={{ fontSize: 14, color: "text.secondary", padding: "8px 0" }}>
                {EMPTY_MESSAGE}
            </Typography>
        );
    } else {
        // <= 3 racks: largura fixa alinhada à esquerda; > 3: item preenche a coluna do grid.
        const isLeftAligned = racks.length <= LEFT_ALIGN_THRESHOLD;
        const containerSx = isLeftAligned ? GAUGES_LEFT_SX : GAUGES_GRID_SX;
        const itemSx = isLeftAligned
            ? { display: "flex", width: GAUGE_ITEM_WIDTH }
            : { display: "flex", minWidth: 0 };
        content = (
            <Box sx={containerSx}>
                {racks.map((rack, index) => (
                    <Box key={`${rack.reference ?? "na"}-${index}`} sx={itemSx}>
                        <AStringCurrentGauge
                            name={`${BMS_LABEL_PREFIX}${formatBmsNumber(rack.reference)}`}
                            valueAmps={rack.socValue}
                            fillRatio={resolveFillRatio(rack.socValue)}
                            visualState={resolveGaugeVisualState(rack.status)}
                            unitLabel={SOC_UNIT}
                        />
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <ACard
            ariaLabel={"bess-bms-soc"}
            title={SECTION_TITLE}
            subTitle={subtitle}
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: BatteryChargingFullOutlinedIcon,
                iconTitleStyle: { color: TITLE_ICON_COLOR, fontSize: 28 },
            }}
            headerControlsPosition={"header"}
            styleProps={{
                cardStyle: { height: "auto" },
                contentStyle: { padding: "0 16px 16px" },
                titleStyle: { alignItems: "center", display: "flex" },
                subTitleStyle: subtitle
                    ? { fontSize: 14, color: "rgba(0, 0, 0, 0.6)", padding: "0 8px 4px" }
                    : { display: "none" },
                headerStyle: { flexDirection: "row" },
                headerActionsStyle: { alignItems: "center", display: "flex" },
            }}
            headerControls={<Legend />}
        >
            {content}
        </ACard>
    );
};

export default BessBmsSocSection;
